import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { SplashScreen } from 'src/components/loading-screen';

import { Role } from 'src/type';
import { useAuthContext } from '../hooks';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
  allowedRoles?: Role[];
};

export default function AuthGuard({ children, allowedRoles }: Props) {
  const { loading } = useAuthContext();

  // Show loading screen while auth is being determined
  if (loading) {
    return <SplashScreen />;
  }

  return <Container allowedRoles={allowedRoles}>{children}</Container>;
}

// ----------------------------------------------------------------------

function Container({ children, allowedRoles }: Props) {
  const router = useRouter();
  const { authenticated, user, loading } = useAuthContext();
  const [checked, setChecked] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const check = useCallback(() => {
    console.log('🛡️ Auth guard check:', { loading, authenticated, redirecting });

    // Don't redirect if still loading or already redirecting
    if (loading || redirecting) {
      console.log('⏳ Auth guard waiting - loading or redirecting');
      return;
    }

    if (!authenticated) {
      console.log('❌ Not authenticated, setting up redirect...');
      setRedirecting(true);
      // Add a longer delay to ensure auth initialization is complete
      setTimeout(() => {
        // Double check authentication state after delay
        if (!loading && !authenticated) {
          console.log('🔄 Redirecting to login page');
          const searchParams = new URLSearchParams({
            returnTo: window.location.pathname,
          }).toString();

          const href = `${paths.auth.login}?${searchParams}`;
          router.replace(href);
        } else {
          console.log('✅ Authentication restored during delay');
        }
        setRedirecting(false);
      }, 1000); // Increased delay to 1 second
    } else {
      console.log('✅ Authenticated, allowing access');
      setChecked(true);
    }
  }, [authenticated, router, loading, redirecting]);

  const checkRole = useCallback(() => {
    if (!allowedRoles) {
      return true;
    }

    if (!user?.currentRole?.role) {
      router.replace('/no-access');
      return false;
    }

    const hasPermission = allowedRoles.includes(user.currentRole.role as Role);
    if (!hasPermission) {
      router.replace('/no-access');
      return false;
    }

    return true;
  }, [user, router, allowedRoles]);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    if (checked) {
      checkRole();
    }
  }, [checked, checkRole]);

  if (!checked) {
    return null;
  }

  return <>{children}</>;
}
